import type { RetainerAgreementWithFileDetails } from '@common/types/retainerAgreements';
import type { DraftRetainerPreviewParams } from '@common/types/retainerPreview';
import { getLegalDocPrefillWidget } from '@common/utils/legalDocPrefills';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import { Button, Link, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { type Dispatch, type SetStateAction, useState } from 'react';
import NewProspectStep from '../NewProspectStep';
import ToggleSection from '../ToggleSection';
import DocumentPreviewModal from './DocumentPreviewModal';
import DocumentSection, { type UploadedDocument } from './DocumentSection';
import ExtraPrefillField from './ExtraPrefillField';
import FileItemIcon from './FileItemIcon';
import RetainerLabel from './RetainerLabel';

interface Props {
    matchingRetainers: RetainerAgreementWithFileDetails[];
    retainerAgreement?: RetainerAgreementWithFileDetails;
    selectedRetainerId: number | null;
    onSelectRetainer(id: number): void;
    draftRetainerPreview: DraftRetainerPreviewParams;
    isRetainerAgreementEnabled: boolean;
    setIsRetainerAgreementEnabled: Dispatch<SetStateAction<boolean>>;
    legalDocTags: string[];
    legalDocValues: Record<string, string>;
    legalDocErrors: Record<string, string>;
    onLegalDocValueChange(tag: string, value: string): void;
    documents: UploadedDocument[];
    onDocumentsChange(files: UploadedDocument[]): void;
    templateName?: string;
    /** The template's default retainer, which gets a Template Default badge */
    templateRetainerId: number | null;
    resetErrors(): void;
}

const supportLink = (
    <Link
        href="mailto:support@lexo.ai"
        target="_blank"
        rel="noopener"
        sx={{ whiteSpace: 'nowrap' }}
    >
        Lexo Support
    </Link>
);

export default function RetainerAgreement({
    matchingRetainers,
    retainerAgreement,
    selectedRetainerId,
    onSelectRetainer,
    draftRetainerPreview,
    isRetainerAgreementEnabled,
    setIsRetainerAgreementEnabled,
    legalDocTags,
    legalDocValues,
    legalDocErrors,
    onLegalDocValueChange,
    documents,
    onDocumentsChange,
    templateName,
    templateRetainerId,
    resetErrors,
}: Props) {
    const [openModal, setOpenModal] = useState(false);

    return (
        <>
            {openModal && retainerAgreement && (
                <DocumentPreviewModal
                    fileId={retainerAgreement.fileId}
                    draftPreview={draftRetainerPreview}
                    onClose={() => setOpenModal(false)}
                />
            )}
            <NewProspectStep
                stepNumber="4"
                title="Documents Setup"
                description="Configure retainer agreement and additional documents"
                showStatus={false}
            >
                {matchingRetainers.length > 0 && (
                    <>
                        <ToggleSection
                            enabled={isRetainerAgreementEnabled}
                            label="Send Retainer Agreement/Fintrac Consent form"
                            description="Include retainer document in client communications and intake form"
                            statusText={isRetainerAgreementEnabled ? 'Enabled' : 'Disabled'}
                            icon="📄"
                            variant="green"
                            onToggle={() => {
                                resetErrors();
                                setIsRetainerAgreementEnabled((prev) => !prev);
                            }}
                        />
                        {isRetainerAgreementEnabled && retainerAgreement && (
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 1.5,
                                    mb: 1,
                                    background: '#f8f9fa',
                                    borderColor: '#e8eaed',
                                    borderRadius: '6px',
                                }}
                            >
                                <Stack spacing={2}>
                                    <Stack
                                        direction="row"
                                        spacing={1.5}
                                        alignItems="center"
                                        justifyContent="space-between"
                                    >
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                            sx={{ flex: 1, minWidth: 0 }}
                                        >
                                            <FileItemIcon fileType={retainerAgreement.fileType} />
                                            {matchingRetainers.length > 1 ? (
                                                <TextField
                                                    select
                                                    size="small"
                                                    sx={{ width: '100%', maxWidth: 420 }}
                                                    value={
                                                        selectedRetainerId != null
                                                            ? String(selectedRetainerId)
                                                            : ''
                                                    }
                                                    onChange={(event) => {
                                                        onSelectRetainer(
                                                            Number(event.target.value),
                                                        );
                                                    }}
                                                    label="Legal document"
                                                >
                                                    {matchingRetainers.map((retainer) => (
                                                        <MenuItem
                                                            key={retainer.retainerAgreementId}
                                                            value={String(
                                                                retainer.retainerAgreementId,
                                                            )}
                                                        >
                                                            <RetainerLabel
                                                                retainer={retainer}
                                                                isTemplateDefault={
                                                                    retainer.retainerAgreementId ===
                                                                    templateRetainerId
                                                                }
                                                            />
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            ) : (
                                                <RetainerLabel
                                                    retainer={retainerAgreement}
                                                    isTemplateDefault={
                                                        retainerAgreement.retainerAgreementId ===
                                                        templateRetainerId
                                                    }
                                                />
                                            )}
                                        </Stack>
                                        <Button
                                            size="small"
                                            startIcon={<FindInPageIcon />}
                                            onClick={() => setOpenModal(true)}
                                        >
                                            Preview
                                        </Button>
                                    </Stack>

                                    {legalDocTags.length > 0 ? (
                                        <Typography variant="caption" color="#5f6368">
                                            These values will be automatically populated in the
                                            document and visible to the client before digital
                                            signing. If additional fields are required, contact{' '}
                                            {supportLink} for one time setup and activation.
                                        </Typography>
                                    ) : (
                                        <Typography variant="caption" color="#5f6368">
                                            Client, lawyer, and matter details are automatically
                                            populated in this document. If it needs other details,
                                            such as a retainer amount or closing date, contact{' '}
                                            {supportLink} for one time setup and those fields will
                                            appear here to fill in.
                                        </Typography>
                                    )}

                                    {legalDocTags.length > 0 && (
                                        <Stack
                                            direction={{ xs: 'column', md: 'row' }}
                                            spacing={2}
                                            useFlexGap
                                            flexWrap="wrap"
                                            alignItems={{ xs: 'stretch', md: 'flex-start' }}
                                            justifyContent="flex-start"
                                            width="100%"
                                        >
                                            {legalDocTags.map((tag) => (
                                                <ExtraPrefillField
                                                    key={tag}
                                                    tag={tag}
                                                    widget={getLegalDocPrefillWidget(tag)}
                                                    value={legalDocValues[tag] ?? ''}
                                                    error={legalDocErrors[tag]}
                                                    onChange={(value) =>
                                                        onLegalDocValueChange(tag, value)
                                                    }
                                                />
                                            ))}
                                        </Stack>
                                    )}
                                </Stack>
                            </Paper>
                        )}
                    </>
                )}
                <Paper
                    variant="outlined"
                    sx={{
                        borderColor: '#e8eaed',
                        borderRadius: '8px',
                        overflow: 'hidden',
                    }}
                >
                    <DocumentSection
                        templateName={templateName}
                        value={documents}
                        onValueChange={(files) => {
                            resetErrors();
                            onDocumentsChange(files);
                        }}
                    />
                </Paper>
            </NewProspectStep>
        </>
    );
}
